import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingCart, X, Truck } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { fmtPrice } from "@/lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, shipping, total, FREE_SHIPPING } = useCart();
  const navigate = useNavigate();
  const remaining = Math.max(0, FREE_SHIPPING - subtotal);

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-4 border-b bg-navy">
          <SheetTitle className="flex items-center gap-2 text-white font-display">
            <ShoppingCart className="h-5 w-5 text-gold" /> Coșul meu
          </SheetTitle>
          <SheetDescription className="sr-only">Produsele adăugate în coșul de cumpărături</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
            <ShoppingCart className="h-12 w-12 text-slate-300" />
            <p className="font-display font-bold text-navy">Coșul tău este gol</p>
            <p className="text-sm text-muted-foreground">Adaugă produse pentru a comanda.</p>
            <button
              onClick={() => setOpen(false)}
              className="mt-2 bg-eco hover:bg-green-700 text-white font-bold px-6 py-2.5 rounded-xl transition-colors"
              data-testid="cart-continue-shopping"
            >
              Vezi produsele
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {items.map((i) => (
                <div
                  key={i.product_id}
                  className="flex gap-3 bg-slate-50 rounded-xl p-3"
                  data-testid={`cart-item-${i.product_id}`}
                >
                  <img src={i.image} alt={i.name} className="h-20 w-20 object-contain rounded-lg bg-white" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="font-display font-bold text-navy text-sm leading-tight">{i.name}</p>
                      <button onClick={() => removeItem(i.product_id)} data-testid={`cart-remove-${i.product_id}`}>
                        <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">{i.volume} · {fmtPrice(i.price)} lei</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border rounded-lg bg-white">
                        <button className="p-1.5" onClick={() => updateQty(i.product_id, i.quantity - 1)} data-testid={`cart-dec-${i.product_id}`}>
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-7 text-center text-sm font-bold">{i.quantity}</span>
                        <button className="p-1.5" onClick={() => updateQty(i.product_id, i.quantity + 1)} data-testid={`cart-inc-${i.product_id}`}>
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="font-display font-extrabold text-navy">{fmtPrice(i.price * i.quantity)} lei</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t px-6 py-4 space-y-3 bg-white">
              {remaining > 0 ? (
                <div className="flex items-center gap-2 text-xs bg-gold/20 text-navy rounded-lg px-3 py-2">
                  <Truck className="h-4 w-4" />
                  Mai adaugă <b>{fmtPrice(remaining)} lei</b> pentru transport gratuit!
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs bg-eco/10 text-eco rounded-lg px-3 py-2 font-bold">
                  <Truck className="h-4 w-4" /> Transport gratuit aplicat!
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-bold">{fmtPrice(subtotal)} lei</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Transport</span>
                <span className="font-bold">{shipping === 0 ? "Gratuit" : `${fmtPrice(shipping)} lei`}</span>
              </div>
              <div className="flex justify-between font-display font-black text-navy text-lg pt-1 border-t">
                <span>Total</span>
                <span data-testid="cart-total">{fmtPrice(total)} lei</span>
              </div>
              <button
                onClick={goCheckout}
                className="w-full bg-eco hover:bg-green-700 text-white font-display font-black py-3.5 rounded-xl transition-colors"
                data-testid="cart-checkout-button"
              >
                FINALIZEAZĂ COMANDA
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
